/**
 * JKL Buy Button Sticky
 *
 * Observes the natural position of .buy-button-sticky relative to the viewport:
 *  - Before it scrolls into view  → buy-button-sticky--bottom (fixed to bottom)
 *  - While it is in its natural position → no class (position: static)
 *  - After it has scrolled out of view above → buy-button-sticky--top (fixed to top)
 */

(function () {
  const CLASS_TOP = "buy-button-sticky--top";
  const CLASS_BOTTOM = "buy-button-sticky--bottom";
  const INIT_ATTR = "data-jkl-sticky-init";

  function initBuyButtonSticky(stickyEl) {
    if (stickyEl.hasAttribute(INIT_ATTR)) {
      return;
    }
    stickyEl.setAttribute(INIT_ATTR, "1");

    // Placeholder element that preserves the original space in the document flow
    // so layout doesn't jump when the button becomes fixed.
    const placeholder = document.createElement("div");
    placeholder.style.display = "none";
    stickyEl.parentNode.insertBefore(placeholder, stickyEl);

    const headerEl = document.querySelector("store-header");

    // Returns the current header height (0 if no header found).
    function getHeaderHeight() {
      return headerEl ? headerEl.offsetHeight : 0;
    }

    // Returns true unless --header-is-visible is explicitly "0" (set by StoreHeader.hide).
    // The variable is NOT set on initial load (header is visible by default),
    // and is never set at all when hide-on-scroll is disabled.
    function isHeaderVisible() {
      return (
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-is-visible")
          .trim() !== "0"
      );
    }

    // Applies (or clears) the top offset so the button sits below the header
    // when CLASS_TOP is active and the header is shown.
    function applyTopOffset() {
      if (stickyEl.classList.contains(CLASS_TOP)) {
        stickyEl.style.top = isHeaderVisible() ? getHeaderHeight() + "px" : "0";
      } else {
        stickyEl.style.top = "";
      }
    }

    function update() {
      // Only run on mobile (mirrors the CSS media query)
      if (window.innerWidth >= 1024) {
        stickyEl.classList.remove(CLASS_TOP, CLASS_BOTTOM);
        stickyEl.style.top = "";
        placeholder.style.display = "none";
        placeholder.style.height = "0";
        return;
      }

      const elementHeight = stickyEl.offsetHeight;
      const viewportHeight = window.innerHeight;

      // When the button is fixed, the placeholder holds its natural space in the flow.
      // When the button is static (no modifier class), measure it directly.
      const isFixed =
        stickyEl.classList.contains(CLASS_TOP) ||
        stickyEl.classList.contains(CLASS_BOTTOM);
      const naturalRect = isFixed
        ? placeholder.getBoundingClientRect()
        : stickyEl.getBoundingClientRect();

      if (naturalRect.top > viewportHeight) {
        // Element is below the viewport — fix to bottom
        placeholder.style.display = "block";
        placeholder.style.height = elementHeight + "px";
        stickyEl.classList.remove(CLASS_TOP);
        stickyEl.classList.add(CLASS_BOTTOM);
      } else if (naturalRect.bottom < 0) {
        // Element is above the viewport — fix to top
        placeholder.style.display = "block";
        placeholder.style.height = elementHeight + "px";
        stickyEl.classList.remove(CLASS_BOTTOM);
        stickyEl.classList.add(CLASS_TOP);
      } else {
        // Element is in its natural position within the viewport — release
        stickyEl.classList.remove(CLASS_TOP, CLASS_BOTTOM);
        placeholder.style.display = "none";
        placeholder.style.height = "0";
      }

      applyTopOffset();
    }

    // Watch for header show/hide by observing --header-is-visible on <html>.
    // StoreHeader.show() sets it to "1", StoreHeader.hide() sets it to "0".
    const headerVisibilityObserver = new MutationObserver(applyTopOffset);
    headerVisibilityObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    update();
  }

  function init() {
    const stickyEl = document.querySelector(".buy-button-sticky");
    if (stickyEl) {
      initBuyButtonSticky(stickyEl);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
